import {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {addSong, setPlayerSong, songsRedraw} from '../store/actions/Actions';
import {setMusicKitPlaylistMeta, setMusicKitPlaylistTracks} from '../store/actions/MusicKitActions';
import {addToPlaylist, getMusicInstance, getPlaylist, getSongsFromPlaylist, isAutoplayEnabled, searchSong} from '../libraries/MusicKit';
import {addCatalog, trackPlay} from '../libraries/DnaApi';
import {isSpotifyConnected} from '../libraries/SpotifyAuth';
import {
    connectSpotify, getSpotifyState, onSpotifyState, pauseSpotify, playSpotifyTrack,
    resolveSpotifyTrack, resumeSpotify, seekSpotifyMs, setSpotifyVolume,
} from '../libraries/SpotifyPlayback';

/**
 * The player "engine": all MusicKit / <audio> binding and transport logic,
 * lifted out of ApplePlayer so the redesigned Ambient surface can drive the
 * same playback without duplicating the fragile configure/bind lifecycle.
 *
 * The classic ApplePlayer keeps its own copy untouched; the two never mount at
 * the same time (separate routes), so there's no double-binding.
 *
 * The consumer must render a single <audio> element wired to `audioRef` /
 * `previewSong`, and attach `progressRef` to the scrubber track for `seek()`.
 */
export default function usePlayerEngine() {
    const music = getMusicInstance();
    const audio = useRef();
    const progressRef = useRef();

    const dispatch = useDispatch();
    const playingSong = useSelector((s) => s.playerSong);
    const playerQueue = useSelector((s) => s.playerSong.playlist);
    const songArtistTitle = useSelector((s) => s.playerSong.title);
    const applePlaylist = useSelector((s) => s.musicKit.playlist);

    const [playbackTime, setPlaybackTime] = useState(0);
    const [isAppleAuthorized, setIsAppleAuthorized] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolumeState] = useState(1);
    const [previewSong, setPreviewSong] = useState(null);
    const [appleSong, setAppleSong] = useState(null);
    const [musicReady, setMusicReady] = useState(false);

    const isCatalog = playingSong.type === 'catalog';

    // The MusicKit/audio event handlers are bound once at mount, but next()
    // closes over the current queue — reach it through a ref so end-of-track
    // advance always sees the live Up Next list.
    const nextRef = useRef(() => {});

    // --- Spotify (Premium full tracks; Apple stays the fallback) ------------
    // sourceRef says which stack owns the CURRENT track: 'apple' | 'spotify'
    // (type 'song' uploads keep their own <audio> branch and ignore it).
    const [spotifyActive, setSpotifyActive] = useState(false);
    const [spotifyTrack, setSpotifyTrack] = useState(null);
    const sourceRef = useRef('apple');
    const spotifyMetaRef = useRef({duration: 0});
    const preMuteVolRef = useRef(1);

    useEffect(() => {
        if (!isSpotifyConnected()) {
            return;
        }
        let cancelled = false;
        connectSpotify().then((ok) => {
            if (!cancelled) {
                setSpotifyActive(ok);
            }
        });
        const off = onSpotifyState(({state, ended}) => {
            if (sourceRef.current !== 'spotify') {
                return;
            }
            setIsPlaying(!state.paused);
            spotifyMetaRef.current.duration = state.duration || 0;
            setPlaybackTime((state.position || 0) / 1000);
            if (ended) {
                nextRef.current();
            }
        });
        return () => {
            cancelled = true;
            off();
        };
    }, []);

    // Progress heartbeat while Spotify owns playback (the SDK only emits
    // state events on changes, not per second).
    useEffect(() => {
        if (!spotifyActive) {
            return;
        }
        const timer = setInterval(() => {
            if (sourceRef.current !== 'spotify') {
                return;
            }
            getSpotifyState().then((state) => {
                if (state && !state.paused) {
                    spotifyMetaRef.current.duration = state.duration || 0;
                    setPlaybackTime((state.position || 0) / 1000);
                }
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [spotifyActive]);

    /**
     * MusicKit configures asynchronously (App.js fetches the developer token
     * from the backend before calling configure). Wait until it's actually
     * configured before binding, otherwise getMusicInstance() hands us the
     * no-op stub and the player stays dead.
     */
    useEffect(() => {
        let cancelled = false;
        const check = () => {
            try {
                if (window.MusicKit?.getInstance?.()) {
                    if (!cancelled) setMusicReady(true);
                    return true;
                }
            } catch (e) {
            }
            return false;
        };
        if (check()) {
            return;
        }
        window.addEventListener('musickitconfigured', check);
        return () => {
            cancelled = true;
            window.removeEventListener('musickitconfigured', check);
        };
    }, []);

    useEffect(() => {
        if (!musicReady) {
            return;
        }
        setIsLoading(true);

        if (music) {
            /* Dev settings */
            music.stop();

            if (!isAutoplayEnabled()) {
                /**
                 * Important hack for Safari
                 */
                setIsMuted(true);
                music.mute();
            }
            setIsAppleAuthorized(music.isAuthorized);
            setIsLoading(false);

            music.addEventListener('playbackProgressDidChange', onPlaybackProgressDidChange);
            music.addEventListener('nowPlayingItemDidChange', onNowPlayingItemDidChange);
            music.addEventListener('playbackStateDidChange', onPlaybackStateDidChange);
            music.addEventListener('queueIsReady', onQueueIsReady);
            music.addEventListener('queueItemsDidChange', onQueueItemsDidChange);
            music.addEventListener('queuePositionDidChange', onQueuePositionDidChange);
        } else {
            console.error("MusicKit library hasn't started on time");
        }

        return () => {
            if (music) {
                music.removeEventListener('playbackProgressDidChange', onPlaybackProgressDidChange);
                music.removeEventListener('nowPlayingItemDidChange', onNowPlayingItemDidChange);
                music.removeEventListener('playbackStateDidChange', onPlaybackStateDidChange);
                music.removeEventListener('queueIsReady', onQueueIsReady);
                music.removeEventListener('queueItemsDidChange', onQueueItemsDidChange);
                music.removeEventListener('queuePositionDidChange', onQueuePositionDidChange);

                music.cleanup();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [musicReady]);

    // <audio> events for uploaded songs (mirror MusicKit's progress/state events).
    useEffect(() => {
        const el = audio.current;
        if (!el) {
            return;
        }
        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onTime = () => setPlaybackTime(el.currentTime);
        const onEnded = () => nextRef.current();
        el.addEventListener('play', onPlay);
        el.addEventListener('pause', onPause);
        el.addEventListener('timeupdate', onTime);
        el.addEventListener('ended', onEnded);
        return () => {
            el.removeEventListener('play', onPlay);
            el.removeEventListener('pause', onPause);
            el.removeEventListener('timeupdate', onTime);
            el.removeEventListener('ended', onEnded);
        };
    }, []);

    useEffect(() => {
        if (!audio.current) {
            return;
        }
        if (previewSong) {
            audio.current.play();
            setIsLoading(false);
        } else {
            audio.current.pause();
        }
    }, [previewSong]);

    useEffect(() => {
        if (appleSong) {
            setIsLoading(false);
        } else {
            music.stop();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appleSong]);

    // Every catalog play feeds the welcome shelves (history, top artists).
    useEffect(() => {
        if (playingSong.id && playingSong.type === 'catalog') {
            trackPlay(playingSong.id, playingSong.intentional);
        }
    }, [playingSong.id, playingSong.type]);

    useEffect(() => {
        if (playingSong.id) {
            setIsLoading(true);

            if (playingSong.type === 'song') {
                setAppleSong(null);
                setPreviewSong(playingSong.song_url);
            } else {
                setPreviewSong(null);
                playSong(playingSong.title, playingSong.apple_id);
            }
        }
        // Key on the song id only: swapping the queue (Up Next radio) updates
        // playerSong.playlist but must not reload/restart the current track.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playingSong.id]);

    // Resolve the NEXT queue item's Apple song while the current one plays, so
    // auto-advance skips the Apple search round trip (it was ~a quarter of the
    // gap between tracks).
    const prefetchRef = useRef(null);
    useEffect(() => {
        if (!playingSong.id || !Array.isArray(playerQueue) || playerQueue.length < 2) {
            return;
        }
        const index = playerQueue.findIndex((song) => song.id === playingSong.id);
        const upcoming = playerQueue[(index + 1) % playerQueue.length];
        if (!upcoming || upcoming.type === 'song' || !upcoming.title || upcoming.apple_id) {
            return; // direct-id playback needs no lookup
        }
        if (spotifyActive) {
            // Warm the Spotify resolve cache (it memoizes internally).
            resolveSpotifyTrack(upcoming.title).catch(() => {});
            return;
        }
        if (prefetchRef.current?.key === upcoming.title) {
            return;
        }
        searchSong(upcoming.title).then((appleSong) => {
            prefetchRef.current = {key: upcoming.title, appleSong};
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playingSong.id, playerQueue]);

    useEffect(() => {
        let poll = null;
        if (isAppleAuthorized) {
            getPlaylist().then((playlist) => {
                dispatch(setMusicKitPlaylistMeta(playlist));

                getSongsFromPlaylist().then((tracks) => {
                    dispatch(setMusicKitPlaylistTracks(tracks));
                });
            });

            poll = setInterval(() => {
                getSongsFromPlaylist().then((tracks) => {
                    dispatch(setMusicKitPlaylistTracks(tracks));
                });
            }, 15000);
        } else {
            clearTimeout(poll);
        }

        return () => {
            clearTimeout(poll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAppleAuthorized]);

    /**
     * Events
     */
    const onPlaybackProgressDidChange = () => {
        setPlaybackTime(music.currentPlaybackTime);
    };

    const onNowPlayingItemDidChange = () => {
        setIsLoading(false);
        setIsPlaying(!!music.isPlaying);
    };

    // Auto-advance must survive every way MusicKit ends a track: full songs
    // exhaust the queue (state 10), 30s previews report 'ended' (5) or just
    // stop a beat early as paused/stopped with under a second left. Two
    // guards keep it honest: at most one advance per APP-LEVEL song (MusicKit
    // item ids are unreliable mid-switch), and a settling window, because the
    // switch itself emits paused/ended states while the properties still hold
    // the previous track's end-of-song values (this once triple-skipped).
    const advancedForRef = useRef(null);
    const lastAdvanceAtRef = useRef(0);
    const playingIdRef = useRef(null);
    const maybeAdvance = () => {
        const now = Date.now();
        if (now - lastAdvanceAtRef.current < 5000) {
            return; // track-switch turbulence, not a real ending
        }
        if (advancedForRef.current === playingIdRef.current) {
            return; // already advanced away from this song
        }
        advancedForRef.current = playingIdRef.current;
        lastAdvanceAtRef.current = now;
        nextRef.current();
    };

    const onPlaybackStateDidChange = () => {
        setIsPlaying(!!music.isPlaying);
        const state = music.playbackState;
        if (state === 10 || state === 5) {
            maybeAdvance();
            return;
        }
        // Preview stall: MusicKit sometimes parks ~1s before the end instead
        // of reporting completion. Paused/stopped that close to the end of a
        // real duration is a finished track, not a user pause.
        if ((state === 3 || state === 4)
            && music.currentPlaybackDuration > 0
            && music.currentPlaybackTimeRemaining <= 1.5) {
            maybeAdvance();
        }
    };

    const onQueueIsReady = () => {};
    const onQueueItemsDidChange = () => {};
    const onQueuePositionDidChange = () => {};

    const playSong = (artist_title, appleId) => {
        if (!artist_title && !appleId) {
            console.error('artist - title not found');
            return;
        }
        // Premium Spotify device ready → full tracks there; anything that
        // fails to resolve falls straight through to the Apple path.
        if (spotifyActive) {
            resolveSpotifyTrack(artist_title).then((track) => {
                if (!track) {
                    return playAppleSong(artist_title, appleId);
                }
                sourceRef.current = 'spotify';
                try {
                    music.stop();
                } catch (e) {
                }
                setAppleSong(null);
                setSpotifyTrack(track);
                spotifyMetaRef.current.duration = track.duration_ms || 0;
                return playSpotifyTrack(track.uri).then(() => {
                    setIsLoading(false);
                }).catch(() => playAppleSong(artist_title, appleId));
            }).catch(() => playAppleSong(artist_title, appleId));
            return;
        }
        playAppleSong(artist_title, appleId);
    };

    const playResolved = async (appleSong) => {
        if (!appleSong) {
            return;
        }
        if (music.queueIsEmpty) {
            await music.setQueue({song: appleSong.id});
            music.skipToNextItem();
        } else if (!music.nowPlayingItem || appleSong.id !== music.nowPlayingItem.id) {
            await music.playNext({song: appleSong.id});
            music.skipToNextItem();
        }
        setAppleSong(appleSong);
    };

    const playAppleSong = (artist_title, appleId) => {
        //0: 'none', 1: 'loading', 2: 'playing', 3: 'paused', 4: 'stopped', 5: 'ended', 6: 'seeking', 8: 'waiting', 9: 'stalled', 10: 'completed'
        if (music && music.playbackState && [1].includes(music.playbackState)) {
            return; //skip
        }
        sourceRef.current = 'apple';
        setSpotifyTrack(null);
        pauseSpotify();

        const bySearch = () => {
            const cached = prefetchRef.current?.key === artist_title ? prefetchRef.current.appleSong : null;
            return (cached ? Promise.resolve(cached) : searchSong(artist_title)).then(playResolved);
        };

        if (appleId) {
            // Play the EXACT track the row shows — same version, same cover.
            // Search was matching different releases (even live takes).
            playResolved({id: String(appleId)}).catch(() => {
                return artist_title ? bySearch() : null; // storefront miss
            }).catch((e) => console.error(e));
        } else if (artist_title) {
            bySearch().catch((e) => console.error(e));
        } else {
            console.error('artist - title not found');
        }
    };

    const play = () => {
        if (playingSong.type === 'song') {
            music.pause();
            audio.current?.play();
        } else if (sourceRef.current === 'spotify') {
            resumeSpotify();
        } else {
            audio.current?.pause();
            if (music.nowPlayingItem) {
                if (music.isPlaying) {
                    music.pause().then(() => {
                        music.play();
                    });
                } else {
                    music.play();
                }
            } else if (!music.queueIsEmpty) {
                music.changeToMediaItem(music.queue.items[0]);
            }
        }
    };

    const pause = () => {
        if (playingSong.type === 'song') {
            audio.current?.pause();
        } else if (sourceRef.current === 'spotify') {
            pauseSpotify();
        } else {
            if (music.isPlaying) {
                music.pause();
            }
        }
    };

    const stop = () => {
        if (playingSong.type === 'song') {
            audio.current?.pause();
        } else if (sourceRef.current === 'spotify') {
            pauseSpotify();
        } else {
            if (music.isPlaying) {
                music.stop();
            }
        }
    };

    const togglePlay = () => {
        if (isPlaying) {
            pause();
        } else {
            play();
        }
    };

    const previous = () => {
        let index = Array.isArray(playerQueue) ? playerQueue.findIndex((song) => song.id === playingSong.id) : 0;
        let previousIndex = (index - 1) % playerQueue.length;
        if (previousIndex < 0) {
            previousIndex = (playerQueue.length - 2) - previousIndex;
        }

        if (playerQueue && playerQueue[previousIndex]) {
            if (playingSong.parentId) {
                playerQueue[previousIndex].parentId = playingSong.parentId;
            }
            dispatch(setPlayerSong(playerQueue[previousIndex], playerQueue));
        }
    };

    const next = () => {
        let index = Array.isArray(playerQueue) ? playerQueue.findIndex((song) => song.id === playingSong.id) : 0;
        let nextIndex = (index + 1) % playerQueue.length;

        if (playerQueue && playerQueue[nextIndex]) {
            if (playingSong.parentId) {
                playerQueue[nextIndex].parentId = playingSong.parentId;
            }
            dispatch(setPlayerSong(playerQueue[nextIndex], playerQueue));
        }
    };
    nextRef.current = next;
    playingIdRef.current = playingSong.id;

    const seekToFraction = (fraction) => {
        const pct = Math.min(1, Math.max(0, fraction));

        if (isCatalog && sourceRef.current === 'spotify') {
            const durMs = spotifyMetaRef.current.duration;
            if (durMs) {
                seekSpotifyMs(durMs * pct);
                setPlaybackTime((durMs * pct) / 1000); // optimistic
            }
        } else if (isCatalog) {
            if (music.currentPlaybackDuration) {
                const t = music.currentPlaybackDuration * pct;
                music.seekToTime(Math.round(t));
                setPlaybackTime(t); // optimistic — don't wait for the next progress event
            }
        } else if (audio.current && audio.current.duration) {
            audio.current.currentTime = audio.current.duration * pct;
            setPlaybackTime(audio.current.currentTime);
        }
    };

    // Legacy click-to-jump (kept for ApplePlayer); the Ambient scrubber drags.
    const seek = (e) => {
        const bar = progressRef.current;
        if (!bar) {
            return;
        }
        const rect = bar.getBoundingClientRect();
        seekToFraction((e.clientX - rect.left) / rect.width);
    };

    const mute = () => {
        const spotifyOwns = isCatalog && sourceRef.current === 'spotify';
        if (isMuted) {
            setIsMuted(false);
            if (spotifyOwns) {
                setSpotifyVolume(preMuteVolRef.current || 1); // SDK has no mute
            } else if (isCatalog) {
                music.unmute();
            } else if (audio.current) {
                audio.current.muted = false;
            }
        } else {
            setIsMuted(true);
            if (spotifyOwns) {
                preMuteVolRef.current = volume;
                setSpotifyVolume(0);
            } else if (isCatalog) {
                music.mute();
            } else if (audio.current) {
                audio.current.muted = true;
            }
        }
    };

    const setVolume = (value) => {
        const v = Math.min(1, Math.max(0, value));
        setVolumeState(v);
        if (isCatalog && sourceRef.current === 'spotify') {
            setSpotifyVolume(v);
        } else if (isCatalog) {
            if (music) {
                music.volume = v;
            }
        } else if (audio.current) {
            audio.current.volume = v;
        }
        setIsMuted(v === 0);
    };

    const onClickLoginToApple = () => {
        if (!music.isAuthorized) {
            music.authorize().then(() => {
                setIsAppleAuthorized(music.isAuthorized);
            }).catch((e) => {
                console.error(e);
            });
        }
    };

    const onClickLogoutFromApple = () => {
        if (music.isAuthorized) {
            music.unauthorize().then(() => {
                setIsAppleAuthorized(music.isAuthorized);
            }).catch((e) => {
                console.error(e);
            });
        }
    };

    const onClickAddToShortlist = () => {
        if (playingSong && playingSong.type === 'catalog') {
            addCatalog(playingSong.id).then((response) => {
                dispatch(addSong(response));
            });
        }
    };

    /**
     * Add an arbitrary "Artist - Title" lookup to the Apple playlist, handling
     * the auth flow. Used by both the now-playing chip and the Recommended "+".
     */
    const addSongToPlaylist = (artistTitle) => {
        if (!artistTitle) {
            return;
        }
        if (!music.isAuthorized) {
            music.authorize().then(() => {
                addToPlaylist(artistTitle).then(() => {
                    getSongsFromPlaylist();
                });
            }).catch((e) => {
                console.error(e);
            });
        } else {
            addToPlaylist(artistTitle).then(() => {
                dispatch(songsRedraw(true));
            });
        }
    };

    const onClickAddToPlaylist = () => addSongToPlaylist(songArtistTitle);

    const onClickAppleMusicLogo = () => {
        let url = 'https://music.apple.com/us/library/all-playlists/';
        if (applePlaylist) {
            url = 'https://music.apple.com/library/playlist/' + applePlaylist.id;
        }
        window.open(url, '_blank', 'noreferrer');
    };

    const formatTime = (time) => {
        if (!time) {
            return '0:00';
        }
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time - minutes * 60);
        return minutes + ':' + seconds.toString().padStart(2, '0');
    };

    /**
     * Derived now-playing display data (catalog → MusicKit item, song → store).
     */
    const spotifyOwnsTrack = isCatalog && sourceRef.current === 'spotify' && spotifyTrack;
    const nowPlayingItem = isCatalog && !spotifyOwnsTrack && music && music.nowPlayingItem
        ? music.nowPlayingItem : null;

    let title = playingSong.songTitle || null;
    let artist = playingSong.artist || null;
    let coverUrl = playingSong.cover || null;

    if (spotifyOwnsTrack) {
        title = spotifyTrack.name;
        artist = spotifyTrack.artists;
        coverUrl = spotifyTrack.image || playingSong.cover || null;
    }

    if (nowPlayingItem) {
        title = nowPlayingItem.attributes.name;
        artist = nowPlayingItem.attributes.artistName;
        if (window.MusicKit && nowPlayingItem.attributes.artwork) {
            coverUrl = window.MusicKit.formatArtworkURL(nowPlayingItem.attributes.artwork, 600, 600);
        }
    }

    // playbackTime is the re-render heartbeat (set by MusicKit progress events
    // and the <audio> timeupdate listener), so derive the clock from it.
    let currentTime = playbackTime || 0;
    let duration = 0;
    let progress = 0;
    if (spotifyOwnsTrack) {
        duration = (spotifyMetaRef.current.duration || 0) / 1000;
        progress = duration ? currentTime / duration : 0;
    } else if (nowPlayingItem) {
        duration = music.currentPlaybackDuration || 0;
        progress = music.currentPlaybackProgress || 0;
    } else if (!isCatalog && audio.current) {
        duration = audio.current.duration || 0;
        progress = duration ? currentTime / duration : 0;
    }

    return {
        // store-backed
        playingSong,
        playerQueue,
        songArtistTitle,
        applePlaylist,
        // engine state
        isLoading,
        isMuted,
        isPlaying,
        isAppleAuthorized,
        spotifyActive,
        volume,
        // derived now-playing
        title,
        artist,
        coverUrl,
        nowPlayingItem,
        progress,
        currentTime,
        duration,
        // refs for the consumer to wire
        audioRef: audio,
        previewSong,
        progressRef,
        // transport
        play,
        pause,
        stop,
        togglePlay,
        previous,
        next,
        seek,
        seekToFraction,
        mute,
        setVolume,
        // apple / playlist
        onClickLoginToApple,
        onClickLogoutFromApple,
        onClickAddToShortlist,
        onClickAddToPlaylist,
        addSongToPlaylist,
        onClickAppleMusicLogo,
        // util
        formatTime,
    };
}
